import { ComplianceReviewsScreen } from '../../pages/dashboard/compliance/reviews';
import {
  Assets,
  BenefitsAndInsurance,
  ExpenditurePage,
  FactFindLayoutContextComponent,
  HealthDetails,
  Income,
  InitialMeetingSummary,
  Liabilities,
  Nominees,
  PersonalInfo,
  Portfolio,
  PortfolioDetail,
} from '@zeta-commons';
import { LoadingScreen } from '@zeta-core';
import { FactFindType } from '@zeta-types';
import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Outlet, RouteObject } from 'react-router';

import { RouterLinks } from '../../app-route-paths';
import { AuthGuard } from '../../auth/auth-guard';
import ProtectedRoute from '../../auth/protected-route';
import Redirect from '../../components/re-direct';
import ClientProfileLayout from '../../layouts/client-profile-layout';
import { CompactErrorPage } from '../../layouts/compact-layout';
import { DashboardLayout } from '../../layouts/dashboard-layout';
import DashboardContainer from '../../layouts/dashboard-layout/dashboard-container';
import ManagedClientLayout from '../../layouts/managed-client-layout';
import ReportLayout from '../../layouts/report-layout.tsx';
import { SubscriptionLayout } from '../../layouts/subscription-layout';
import { ConsumerDuty } from '../../pages/dashboard/agents';
import ManagePortfolioRoutes from './manage-portfolio-routes';
import NewReview from '../../pages/dashboard/compliance/new-review';
import ComplianceReviewResultsScreen from '../../pages/dashboard/compliance/review-details';

const ClientsList = lazy(() => import('../../pages/dashboard/advice'));
const ClientBillingHome = lazy(() => import('../../pages/dashboard/billing'));
const DocumentManagement = lazy(() => import('../../pages/dashboard/document-management'));
const ClientProfilePage = lazy(() => import('../../pages/dashboard/client-profile'));
const RiskQuestions = lazy(() => import('../../pages/dashboard/client-profile/risk-questions'));
const InviteClient = lazy(() => import('../../pages/dashboard/invite-client'));
const ProtectionPlan = lazy(() => import('../../pages/dashboard/managed-client/protection-plan'));
const MigrateClient = lazy(() => import('../../pages/dashboard/migrate-client'));
const ClientTasks = lazy(() => import('../../pages/dashboard/tasks'));
const CustomTask = lazy(() => import('../../pages/dashboard/tasks/custom-task'));
const FactFindTasks = lazy(() => import('../../pages/dashboard/tasks/fact-find-tasks'));
const ProtectioncreationForms = lazy(() => import('../../pages/dashboard/tasks/protection-creation-forms'));
const Planning = lazy(() => import('../../pages/dashboard/tasks/protection-planning'));
const AIAssistance = lazy(() => import('../../pages/dashboard/client-profile/ai-assistance'));
const Meetings = lazy(() => import('../../pages/dashboard/meetings'));
const MyTasks = lazy(() => import('../../pages/dashboard/my-tasks'));
const Report = lazy(() => import('../../pages/dashboard/my-tasks/report'));
const CreateSolution = lazy(() => import('../../pages/dashboard/my-tasks/protection-planning'));
const NotificationsPage = lazy(() => import('../../pages/dashboard/profile-setup/notifications'));
const AdvisorCopilotPage = lazy(() => import('../../pages/dashboard/advisor-copilot'));
const ClientInfo = lazy(() => import('../../pages/dashboard/client-info'));
const Client360Entry = lazy(() => import('../../pages/dashboard/client-info/client-360-entry'));

const SelectPortfolio = lazy(() => import('../../pages/dashboard/select-portfolio'));
const FundDetailsPage = lazy(() => import('../../pages/dashboard/fund-details'));
const Dashboard = lazy(() => import('../../pages/dashboard/dashboard'));
const PaymentSuccess = lazy(() => import('../../pages/dashboard/payment-success'));
const SecureMessageLayout = lazy(() => import('../../pages/dashboard/client-info/secure-message'));
const ClientInfoKYCAML = lazy(() => import('../../pages/dashboard/client-info/kyc/kyc-aml'));
const InvestmentPlanning = lazy(() => import('../../pages/dashboard/investment-planning'));
const RiskToleranceQuestions = lazy(() => import('../../pages/dashboard/client-profile/risk-questions'));
const GDPRAgreement = lazy(() => import('../../pages/dashboard/client-profile/gdpr'));
const AdvisoryActionsPage = lazy(() => import('../../pages/dashboard/client-info/advisory-actions'));
const Client360ConflictResolutionPage = lazy(() => import('../../pages/dashboard/client-info/conflict-resolution'));
const ClientInteractionHub = lazy(() => import('../../pages/dashboard/client-info/timeline'));
const ClientInfoHouseHold = lazy(() => import('../../pages/dashboard/client-info/household/house_hold'));
const BusinessReviewsPage = lazy(() => import('../../pages/dashboard/client-info/business-reviews'));
const BusinessProfilePage = lazy(() => import('../../pages/dashboard/client-info/business-profile'));
const ClientInfoReports = lazy(() => import('../../pages/dashboard/client-info/reports/index'));
const ClientInfoPlans = lazy(() => import('../../pages/dashboard/client-info/advice-planning-workspace'));
const ClientInfoNewPlan = lazy(() => import('../../pages/dashboard/client-info/advice-planning-workspace/add-new-plan'));
const ClientInfoPlanSetup = lazy(() => import('../../pages/dashboard/client-info/advice-planning-workspace/add-new-plan/step2'));
const ClientInfoFinancialPlanningWorkspace = lazy(() => import('../../pages/dashboard/client-info/advice-planning-workspace/finance'));
const Support = lazy(() => import('../../pages/support'));
const SupportTicket = lazy(() => import('../../pages/support/support-ticket'));
const Training = lazy(() => import('../../pages/support/training'));

const DashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: (
      <ErrorBoundary FallbackComponent={CompactErrorPage}>
        <Suspense fallback={<LoadingScreen />}>
          <AuthGuard>
            <ProtectedRoute>
              <DashboardLayout>
                <Outlet />
              </DashboardLayout>
            </ProtectedRoute>
          </AuthGuard>
        </Suspense>
      </ErrorBoundary>
    ),
    children: [
      { index: true, element: <Navigate to={RouterLinks.advisorCopilot} replace /> },
      {
        path: 'copilot',
        element: (
          <DashboardContainer sx={{ bgcolor: 'background.default' }} disablePadding>
            <AdvisorCopilotPage />
          </DashboardContainer>
        ),
      },
      {
        path: 'copilot/workspaces/:workspaceId',
        element: (
          <DashboardContainer sx={{ bgcolor: 'background.default' }} disablePadding>
            <AdvisorCopilotPage />
          </DashboardContainer>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <DashboardContainer>
            <Dashboard />
          </DashboardContainer>
        ),
      },
      { path: 'notifications', element: <NotificationsPage /> },
      {
        path: 'crm',
        element: <Navigate to={RouterLinks.crm} replace />,
      },
      {
        path: 'clients',
        element: (
          <DashboardContainer sx={{ bgcolor: 'background.contrast' }}>
            <Outlet />
          </DashboardContainer>
        ),
        children: [
          {
            index: true,
            element: <ClientsList />,
          },
          {
            path: 'invite-client',
            element: (
              <DashboardContainer sx={{ bgcolor: 'background.contrast' }} disablePadding>
                <InviteClient />
              </DashboardContainer>
            ),
          },
          {
            path: 'migrate-client',
            element: (
              <SubscriptionLayout requiredFeature="MIGRATION">
                <MigrateClient />
              </SubscriptionLayout>
            ),
          },
        ],
      },
      {
        path: 'support',
        element: (
          <DashboardContainer sx={{ bgcolor: 'background.contrast' }}>
            <Outlet />
          </DashboardContainer>
        ),
        children: [
          { index: true, element: <Support /> },
          { path: 'ticket', element: <SupportTicket /> },
          { path: 'training', element: <Training /> },
        ],
      },
      {
        path: 'meetings',
        element: (
          // <MeetingsLayout>
          //   <Outlet />
          // </MeetingsLayout>
          <Meetings />
        ),
        children: [
          { path: '', element: <Meetings /> },
          // { path: 'availability', element: <Availability /> },
        ],
      },
      {
        path: 'managed-client',
        element: (
          <FactFindLayoutContextComponent>
            <ManagedClientLayout>
              <Suspense fallback={<LoadingScreen />}>
                <Outlet />
              </Suspense>
            </ManagedClientLayout>
          </FactFindLayoutContextComponent>
        ),
        children: [
          { path: 'documents', element: <DocumentManagement /> },
          { path: 'household', element: <ClientInfoHouseHold /> },
          { path: 'plans', element: <ClientInfoPlans /> },
          { path: 'advice-planning-workspace', element: <ClientInfoFinancialPlanningWorkspace /> },
          { path: 'reports', element: <ClientInfoReports /> },
          { path: 'discovery', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'initial-meeting-summary', element: <InitialMeetingSummary from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'personal-information', element: <PersonalInfo from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'income', element: <Income from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'expenditure', element: <ExpenditurePage from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'assets', element: <Assets from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'beneficiaries', element: <BenefitsAndInsurance from={FactFindType.FACT_FIND} /> },
          { path: 'liabilities', element: <Liabilities from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'health-details', element: <HealthDetails from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'nominees', element: <Nominees from={FactFindType.FACT_FIND} isManagedClient /> },
          { path: 'benefits-and-insurance', element: <BenefitsAndInsurance from={FactFindType.FACT_FIND} /> },
          { path: 'client-interaction-hub', element: <ClientInteractionHub from={FactFindType.FACT_FIND} /> },
          //
          { path: 'portfolio', element: <Portfolio /> },
          { path: 'portfolio-detail', element: <PortfolioDetail /> },
          { path: 'protection-plan', element: <ProtectionPlan /> },
          { path: 'secure-message', element: <SecureMessageLayout /> },

          { path: '*', element: <Redirect to={RouterLinks.managedClientInitialMeetingSummary} /> },
        ],
      },
      { path: 'ask-zeta', element: <Navigate to={RouterLinks.advisorCopilot} replace /> },
      { path: 'agents/consumer-duty', element: <ConsumerDuty /> },
      { path: 'compliance/reviews', element: <ComplianceReviewsScreen /> },
      { path: 'compliance/new-review', element: <NewReview /> },
      { path: 'compliance/review-details', element: <ComplianceReviewResultsScreen /> },
      { path: 'agents/illustrations', element: <Navigate to={RouterLinks.advisorCopilot} replace /> },
      {
        path: 'client-info',
        element: (
          <FactFindLayoutContextComponent>
            <ClientInfo />
          </FactFindLayoutContextComponent>
        ),
        children: [
          { index: true, element: <Client360Entry /> },
          { path: 'initial-meeting-summary', element: <InitialMeetingSummary from={FactFindType.PARA_PLANNER} /> },
          { path: 'household', element: <ClientInfoHouseHold /> },
          { path: 'overview', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'advisory-actions', element: <AdvisoryActionsPage /> },
          { path: 'conflict-resolution', element: <Client360ConflictResolutionPage /> },
          { path: 'activity-log', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'ask-zeta', element: <Client360Entry /> },
          { path: 'zeta-copilot', element: <Client360Entry /> },
          { path: 'discovery', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'plans-new/setup', element: <ClientInfoPlanSetup /> },
          { path: 'plans-new/protection', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'plans-new/investment', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'plans-new/retirement', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'plans-new/mortgage', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'plans-new/estate', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'plans-new/tax', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'plans-new', element: <ClientInfoNewPlan /> },
          { path: 'meeting-notes', element: <Navigate to={RouterLinks.clientInfoAdvisoryActions} replace /> },
          { path: 'zeta-ai', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'zeta-ai-advise', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'secure-message', element: <SecureMessageLayout /> },
          { path: 'custom-task', element: <CustomTask /> },
          { path: 'fact-find-tasks', element: <FactFindTasks /> },
          { path: 'protection-planning', element: <Planning /> },
          { path: 'documents', element: <DocumentManagement /> },

          { path: 'plans', element: <ClientInfoPlans /> },
          { path: 'financial-planning-workspace', element: <ClientInfoFinancialPlanningWorkspace /> },
          { path: 'financial-planning-workspace/mock', element: <ClientInfoFinancialPlanningWorkspace /> },
          { path: 'retirement-planning-workspace', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'protection-planning-workspace', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'investment-planning-workspace', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'advice-planning-workspace', element: <ClientInfoFinancialPlanningWorkspace /> },
          { path: 'reports', element: <ClientInfoReports /> },
          { path: 'review', element: <Navigate to={RouterLinks.clientInfoHouseHold} replace /> },
          { path: 'billing', element: <ClientBillingHome /> },
          { path: 'kyc-aml', element: <ClientInfoKYCAML /> },
          { path: 'investment-planning', element: <InvestmentPlanning from={FactFindType.PARA_PLANNER} /> },
          { path: 'solution', element: <ProtectioncreationForms /> },
          { path: 'client-interaction-hub', element: <ClientInteractionHub from={FactFindType.AI} /> },
          { path: 'business-profile', element: <BusinessProfilePage /> },
          { path: 'business-reviews', element: <BusinessReviewsPage /> },
          { path: 'business-reviews/:fbrPlanId', element: <BusinessReviewsPage /> },
          // { path: 'document-management', element: <DocumentManagement /> },
        ],
      },
      {
        path: '',
        element: (
          <ClientProfileLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </ClientProfileLayout>
        ),
        children: [
          { path: 'client-profile', element: <ClientProfilePage /> },
          {
            path: 'client-tasks',
            children: [
              { path: 'custom-task', element: <CustomTask /> },
              { path: 'protection-planning', element: <Planning /> },
              { path: 'solution', element: <ProtectioncreationForms /> },
              {
                path: 'investment-planning',
                element: (
                  <DashboardContainer>
                    <InvestmentPlanning />
                  </DashboardContainer>
                ),
              },
              // {
              //   path: 'goals-timelines',
              //   element: (
              //     <DashboardContainer>
              //       <GoalsAndTimelines />
              //     </DashboardContainer>
              //   ),
              // },

              {
                path: 'select-portfolio',
                element: (
                  <DashboardContainer>
                    <SelectPortfolio />
                  </DashboardContainer>
                ),
              },
              {
                path: 'fund-details',
                element: (
                  <DashboardContainer>
                    <FundDetailsPage />
                  </DashboardContainer>
                ),
              },
              { path: '*', element: <ClientTasks from={FactFindType.FACT_FIND} />, index: true },
            ],
          },
          { path: 'ai-assistance', element: <AIAssistance /> },
        ],
      },
      { path: 'risk-questions', element: <RiskQuestions /> },
      {
        path: 'my-tasks',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <DashboardContainer>
                <MyTasks />
              </DashboardContainer>
            ),
          },
          {
            path: 'create-solution',
            element: (
              <DashboardContainer>
                <CreateSolution />
              </DashboardContainer>
            ),
          },
          {
            path: 'report',
            element: (
              <ReportLayout>
                <Report />
              </ReportLayout>
            ),
          },
          { path: 'risk-questions', element: <RiskToleranceQuestions /> },
          { path: 'gdpr-agreement', element: <GDPRAgreement /> },
        ],
      },
      ...ManagePortfolioRoutes,
    ],
  },
  {
    path: 'complete-payment',
    element: (
      <ErrorBoundary FallbackComponent={CompactErrorPage}>
        <Suspense fallback={<LoadingScreen />}>
          <AuthGuard>
            <ProtectedRoute>
              <DashboardLayout>
                <PaymentSuccess />
              </DashboardLayout>
            </ProtectedRoute>
          </AuthGuard>
        </Suspense>
      </ErrorBoundary>
    ),
  },
];

export default DashboardRoutes;
